import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { OrderApi } from "../../api/OrderApi";
import type { BalanceFetchOptions } from "../../generated/dto/BalanceFetchOptions";
import type { BalanceStaff } from "../../generated/dto/BalanceStaff";
import type { BalanceTransactionFetchOptions } from "../../generated/dto/BalanceTransactionFetchOptions";
import type { BalanceTransactionSearchParams } from "../../generated/dto/BalanceTransactionSearchParams";
import type { BalanceTransactionStaff } from "../../generated/dto/BalanceTransactionStaff";
import type { BalanceTransactionStaffSearchResult } from "../../generated/dto/BalanceTransactionStaffSearchResult";
import type { CardFetchOptions } from "../../generated/dto/CardFetchOptions";
import type { CardStaff } from "../../generated/dto/CardStaff";
import type { CatalogDiscountInstanceFetchOptions } from "../../generated/dto/CatalogDiscountInstanceFetchOptions";
import type { CatalogDiscountInstanceStaff } from "../../generated/dto/CatalogDiscountInstanceStaff";
import type { CatalogItemInstanceFetchOptions } from "../../generated/dto/CatalogItemInstanceFetchOptions";
import type { CatalogItemInstanceStaff } from "../../generated/dto/CatalogItemInstanceStaff";
import type { CatalogTaxFetchOptions } from "../../generated/dto/CatalogTaxFetchOptions";
import type { CatalogTaxStaff } from "../../generated/dto/CatalogTaxStaff";
import type { CheckoutReadiness } from "../../generated/dto/CheckoutReadiness";
import type { DamageWaiverFetchOptions } from "../../generated/dto/DamageWaiverFetchOptions";
import type { DamageWaiverStaff } from "../../generated/dto/DamageWaiverStaff";
import type { DiscountCandidate } from "../../generated/dto/DiscountCandidate";
import type { DonationFetchOptions } from "../../generated/dto/DonationFetchOptions";
import type { DonationStaff } from "../../generated/dto/DonationStaff";
import type { GuestPrivilegeFetchOptions } from "../../generated/dto/GuestPrivilegeFetchOptions";
import type { GuestPrivilegeStaff } from "../../generated/dto/GuestPrivilegeStaff";
import type { LineItemDiscountInstanceFetchOptions } from "../../generated/dto/LineItemDiscountInstanceFetchOptions";
import type { LineItemDiscountInstanceStaff } from "../../generated/dto/LineItemDiscountInstanceStaff";
import type { LineItemFetchOptions } from "../../generated/dto/LineItemFetchOptions";
import type { LineItemRef } from "../../generated/dto/LineItemRef";
import type { LineItemRefundFetchOptions } from "../../generated/dto/LineItemRefundFetchOptions";
import type { LineItemRefundStaff } from "../../generated/dto/LineItemRefundStaff";
import type { LineItemRefundTaxFetchOptions } from "../../generated/dto/LineItemRefundTaxFetchOptions";
import type { LineItemRefundTaxStaff } from "../../generated/dto/LineItemRefundTaxStaff";
import type { LineItemStaff } from "../../generated/dto/LineItemStaff";
import type { LineItemTaxFetchOptions } from "../../generated/dto/LineItemTaxFetchOptions";
import type { LineItemTaxStaff } from "../../generated/dto/LineItemTaxStaff";
import type { MembershipStaff } from "../../generated/dto/MembershipStaff";
import type { MembershipStaffFetchOptions } from "../../generated/dto/MembershipStaffFetchOptions";
import type { OrderAddPurchaseRequest } from "../../generated/dto/OrderAddPurchaseRequest";
import type { OrderAddToCartRequest } from "../../generated/dto/OrderAddToCartRequest";
import type { OrderApplyDiscountFromCodeRequest } from "../../generated/dto/OrderApplyDiscountFromCodeRequest";
import type { OrderApplyDiscountToLineItemsRequest } from "../../generated/dto/OrderApplyDiscountToLineItemsRequest";
import type { OrderChangeBuyerRequest } from "../../generated/dto/OrderChangeBuyerRequest";
import type { OrderConfigureDeliveryRequest } from "../../generated/dto/OrderConfigureDeliveryRequest";
import type { OrderConfigureReceiptDeliveryRequest } from "../../generated/dto/OrderConfigureReceiptDeliveryRequest";
import type { OrderFetchOptions } from "../../generated/dto/OrderFetchOptions";
import type { OrderLineDetail } from "../../generated/dto/OrderLineDetail";
import type { OrderMember } from "../../generated/dto/OrderMember";
import type { OrderOpenOrderRequest } from "../../generated/dto/OrderOpenOrderRequest";
import type { OrderPaymentFetchOptions } from "../../generated/dto/OrderPaymentFetchOptions";
import type { OrderPaymentStaff } from "../../generated/dto/OrderPaymentStaff";
import type { OrderRef } from "../../generated/dto/OrderRef";
import type { OrderRefundFetchOptions } from "../../generated/dto/OrderRefundFetchOptions";
import type { OrderRefundStaff } from "../../generated/dto/OrderRefundStaff";
import type { OrderSearchParams } from "../../generated/dto/OrderSearchParams";
import type { OrderStaff } from "../../generated/dto/OrderStaff";
import type { OrderStaffSearchResult } from "../../generated/dto/OrderStaffSearchResult";
import type { OrderSwitchOrderSourceRequest } from "../../generated/dto/OrderSwitchOrderSourceRequest";
import type { OrderUnapplyDiscountFromLineItemsRequest } from "../../generated/dto/OrderUnapplyDiscountFromLineItemsRequest";
import type { OrderUnapplyLineItemDiscountRequest } from "../../generated/dto/OrderUnapplyLineItemDiscountRequest";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonRef } from "../../generated/dto/PersonRef";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { PurchaseDeliveryFetchOptions } from "../../generated/dto/PurchaseDeliveryFetchOptions";
import type { PurchaseDeliveryRef } from "../../generated/dto/PurchaseDeliveryRef";
import type { PurchaseDeliveryStaff } from "../../generated/dto/PurchaseDeliveryStaff";
import type { StockChangeFetchOptions } from "../../generated/dto/StockChangeFetchOptions";
import type { StockChangeStaff } from "../../generated/dto/StockChangeStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class OrderApiClient implements OrderApi {
  constructor(private readonly client = useClient()) {}

  async getOrderStaff(id: number, options?: OrderFetchOptions): Promise<OrderStaff> {
    const { data } = await this.client.get<unknown>(`/order/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.OrderFetchOptions") });
    return deserializeCbiDto<OrderStaff>(data, "generated.dto.OrderStaff");
  }

  async getLineItemStaff(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.LineItemFetchOptions") });
    return deserializeCbiDto<LineItemStaff>(data, "generated.dto.LineItemStaff");
  }

  async getLineItemDiscountInstanceStaff(id: number, options?: LineItemDiscountInstanceFetchOptions): Promise<LineItemDiscountInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemdiscountinstance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.LineItemDiscountInstanceFetchOptions") });
    return deserializeCbiDto<LineItemDiscountInstanceStaff>(data, "generated.dto.LineItemDiscountInstanceStaff");
  }

  async getBalanceTransactionStaff(id: number, options?: BalanceTransactionFetchOptions): Promise<BalanceTransactionStaff> {
    const { data } = await this.client.get<unknown>(`/balancetransaction/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.BalanceTransactionFetchOptions") });
    return deserializeCbiDto<BalanceTransactionStaff>(data, "generated.dto.BalanceTransactionStaff");
  }

  async getLineItemTaxStaff(id: number, options?: LineItemTaxFetchOptions): Promise<LineItemTaxStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemtax/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.LineItemTaxFetchOptions") });
    return deserializeCbiDto<LineItemTaxStaff>(data, "generated.dto.LineItemTaxStaff");
  }

  async getLineItemRefundStaff(id: number, options?: LineItemRefundFetchOptions): Promise<LineItemRefundStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemrefund/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.LineItemRefundFetchOptions") });
    return deserializeCbiDto<LineItemRefundStaff>(data, "generated.dto.LineItemRefundStaff");
  }

  async getLineItemRefundTaxStaff(id: number, options?: LineItemRefundTaxFetchOptions): Promise<LineItemRefundTaxStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemrefundtax/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.LineItemRefundTaxFetchOptions") });
    return deserializeCbiDto<LineItemRefundTaxStaff>(data, "generated.dto.LineItemRefundTaxStaff");
  }

  async searchOrderStaff(params: OrderSearchParams, page: number, perPage: number): Promise<PaginationResult<OrderStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/order/staff/search`, serializeCbiDto(params, "generated.dto.OrderSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<OrderStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<OrderStaffSearchResult>>(result.items, "generated.dto.OrderStaffSearchResult") };
    return result;
  }

  async searchBalanceTransactionStaff(params: BalanceTransactionSearchParams, page: number, perPage: number): Promise<PaginationResult<BalanceTransactionStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/balancetransaction/staff/search`, serializeCbiDto(params, "generated.dto.BalanceTransactionSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<BalanceTransactionStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<BalanceTransactionStaffSearchResult>>(result.items, "generated.dto.BalanceTransactionStaffSearchResult") };
    return result;
  }

  async searchOrderStaffFull(params: OrderSearchParams, page: number, perPage: number, options?: OrderFetchOptions): Promise<PaginationResult<OrderStaff>> {
    const { data } = await this.client.post<unknown>(`/order/staff/search/full`, serializeCbiDto(params, "generated.dto.OrderSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.OrderFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<OrderStaff>;
    result = { ...result, items: deserializeCbiDto<Array<OrderStaff>>(result.items, "generated.dto.OrderStaff") };
    return result;
  }

  async searchBalanceTransactionStaffFull(params: BalanceTransactionSearchParams, page: number, perPage: number, options?: BalanceTransactionFetchOptions): Promise<PaginationResult<BalanceTransactionStaff>> {
    const { data } = await this.client.post<unknown>(`/balancetransaction/staff/search/full`, serializeCbiDto(params, "generated.dto.BalanceTransactionSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.BalanceTransactionFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<BalanceTransactionStaff>;
    result = { ...result, items: deserializeCbiDto<Array<BalanceTransactionStaff>>(result.items, "generated.dto.BalanceTransactionStaff") };
    return result;
  }

  async getOrderStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/order/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getOrderStaffLineItems(id: number, options?: LineItemFetchOptions): Promise<Array<LineItemStaff>> {
    const { data } = await this.client.get<unknown>(`/order/staff/${id}/lineitems`, { params: serializeCbiDto(options, "generated.dto.LineItemFetchOptions") });
    return deserializeCbiDto<Array<LineItemStaff>>(data, "generated.dto.LineItemStaff");
  }

  async getOrderStaffOrderRefunds(id: number, options?: OrderRefundFetchOptions): Promise<Array<OrderRefundStaff>> {
    const { data } = await this.client.get<unknown>(`/order/staff/${id}/orderrefunds`, { params: serializeCbiDto(options, "generated.dto.OrderRefundFetchOptions") });
    return deserializeCbiDto<Array<OrderRefundStaff>>(data, "generated.dto.OrderRefundStaff");
  }

  async getOrderStaffPayments(id: number, options?: OrderPaymentFetchOptions): Promise<Array<OrderPaymentStaff>> {
    const { data } = await this.client.get<unknown>(`/order/staff/${id}/payments`, { params: serializeCbiDto(options, "generated.dto.OrderPaymentFetchOptions") });
    return deserializeCbiDto<Array<OrderPaymentStaff>>(data, "generated.dto.OrderPaymentStaff");
  }

  async getLineItemStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getLineItemStaffDonation(id: number, options?: DonationFetchOptions): Promise<DonationStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/donation`, { params: serializeCbiDto(options, "generated.dto.DonationFetchOptions") });
    return deserializeCbiDto<DonationStaff | null>(data, "generated.dto.DonationStaff");
  }

  async getLineItemStaffMembership(id: number, options?: MembershipStaffFetchOptions): Promise<MembershipStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/membership`, { params: serializeCbiDto(options, "generated.dto.MembershipStaffFetchOptions") });
    return deserializeCbiDto<MembershipStaff | null>(data, "generated.dto.MembershipStaff");
  }

  async getLineItemStaffBalanceTransaction(id: number, options?: BalanceTransactionFetchOptions): Promise<BalanceTransactionStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/balancetransaction`, { params: serializeCbiDto(options, "generated.dto.BalanceTransactionFetchOptions") });
    return deserializeCbiDto<BalanceTransactionStaff | null>(data, "generated.dto.BalanceTransactionStaff");
  }

  async getLineItemStaffStockChange(id: number, options?: StockChangeFetchOptions): Promise<StockChangeStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/stockchange`, { params: serializeCbiDto(options, "generated.dto.StockChangeFetchOptions") });
    return deserializeCbiDto<StockChangeStaff | null>(data, "generated.dto.StockChangeStaff");
  }

  async getLineItemStaffCard(id: number, options?: CardFetchOptions): Promise<CardStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/card`, { params: serializeCbiDto(options, "generated.dto.CardFetchOptions") });
    return deserializeCbiDto<CardStaff | null>(data, "generated.dto.CardStaff");
  }

  async getLineItemStaffGuestPrivs(id: number, options?: GuestPrivilegeFetchOptions): Promise<GuestPrivilegeStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/guestprivs`, { params: serializeCbiDto(options, "generated.dto.GuestPrivilegeFetchOptions") });
    return deserializeCbiDto<GuestPrivilegeStaff | null>(data, "generated.dto.GuestPrivilegeStaff");
  }

  async getLineItemStaffDamageWaiver(id: number, options?: DamageWaiverFetchOptions): Promise<DamageWaiverStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/damagewaiver`, { params: serializeCbiDto(options, "generated.dto.DamageWaiverFetchOptions") });
    return deserializeCbiDto<DamageWaiverStaff | null>(data, "generated.dto.DamageWaiverStaff");
  }

  async getLineItemStaffPurchaseDelivery(id: number, options?: PurchaseDeliveryFetchOptions): Promise<PurchaseDeliveryStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/purchasedelivery`, { params: serializeCbiDto(options, "generated.dto.PurchaseDeliveryFetchOptions") });
    return deserializeCbiDto<PurchaseDeliveryStaff | null>(data, "generated.dto.PurchaseDeliveryStaff");
  }

  async getLineItemStaffOrderId(id: number, options?: OrderFetchOptions): Promise<OrderStaff> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/orderid`, { params: serializeCbiDto(options, "generated.dto.OrderFetchOptions") });
    return deserializeCbiDto<OrderStaff>(data, "generated.dto.OrderStaff");
  }

  async getLineItemStaffItemInstanceId(id: number, options?: CatalogItemInstanceFetchOptions): Promise<CatalogItemInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/iteminstanceid`, { params: serializeCbiDto(options, "generated.dto.CatalogItemInstanceFetchOptions") });
    return deserializeCbiDto<CatalogItemInstanceStaff>(data, "generated.dto.CatalogItemInstanceStaff");
  }

  async getLineItemStaffParentItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/parentitemid`, { params: serializeCbiDto(options, "generated.dto.LineItemFetchOptions") });
    return deserializeCbiDto<LineItemStaff | null>(data, "generated.dto.LineItemStaff");
  }

  async getLineItemStaffChildItems(id: number, options?: LineItemFetchOptions): Promise<Array<LineItemStaff>> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/childitems`, { params: serializeCbiDto(options, "generated.dto.LineItemFetchOptions") });
    return deserializeCbiDto<Array<LineItemStaff>>(data, "generated.dto.LineItemStaff");
  }

  async getLineItemStaffDiscountInstances(id: number, options?: LineItemDiscountInstanceFetchOptions): Promise<Array<LineItemDiscountInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/discountinstances`, { params: serializeCbiDto(options, "generated.dto.LineItemDiscountInstanceFetchOptions") });
    return deserializeCbiDto<Array<LineItemDiscountInstanceStaff>>(data, "generated.dto.LineItemDiscountInstanceStaff");
  }

  async getLineItemStaffTaxes(id: number, options?: LineItemTaxFetchOptions): Promise<Array<LineItemTaxStaff>> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/taxes`, { params: serializeCbiDto(options, "generated.dto.LineItemTaxFetchOptions") });
    return deserializeCbiDto<Array<LineItemTaxStaff>>(data, "generated.dto.LineItemTaxStaff");
  }

  async getLineItemStaffRefunds(id: number, options?: LineItemRefundFetchOptions): Promise<Array<LineItemRefundStaff>> {
    const { data } = await this.client.get<unknown>(`/lineitem/staff/${id}/refunds`, { params: serializeCbiDto(options, "generated.dto.LineItemRefundFetchOptions") });
    return deserializeCbiDto<Array<LineItemRefundStaff>>(data, "generated.dto.LineItemRefundStaff");
  }

  async getLineItemDiscountInstanceStaffLineItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemdiscountinstance/staff/${id}/lineitemid`, { params: serializeCbiDto(options, "generated.dto.LineItemFetchOptions") });
    return deserializeCbiDto<LineItemStaff>(data, "generated.dto.LineItemStaff");
  }

  async getLineItemDiscountInstanceStaffDiscountInstanceId(id: number, options?: CatalogDiscountInstanceFetchOptions): Promise<CatalogDiscountInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemdiscountinstance/staff/${id}/discountinstanceid`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountInstanceFetchOptions") });
    return deserializeCbiDto<CatalogDiscountInstanceStaff>(data, "generated.dto.CatalogDiscountInstanceStaff");
  }

  async getBalanceTransactionStaffBalanceId(id: number, options?: BalanceFetchOptions): Promise<BalanceStaff> {
    const { data } = await this.client.get<unknown>(`/balancetransaction/staff/${id}/balanceid`, { params: serializeCbiDto(options, "generated.dto.BalanceFetchOptions") });
    return deserializeCbiDto<BalanceStaff>(data, "generated.dto.BalanceStaff");
  }

  async getLineItemTaxStaffLineItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemtax/staff/${id}/lineitemid`, { params: serializeCbiDto(options, "generated.dto.LineItemFetchOptions") });
    return deserializeCbiDto<LineItemStaff>(data, "generated.dto.LineItemStaff");
  }

  async getLineItemTaxStaffTaxId(id: number, options?: CatalogTaxFetchOptions): Promise<CatalogTaxStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemtax/staff/${id}/taxid`, { params: serializeCbiDto(options, "generated.dto.CatalogTaxFetchOptions") });
    return deserializeCbiDto<CatalogTaxStaff>(data, "generated.dto.CatalogTaxStaff");
  }

  async getLineItemRefundStaffLineItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitemrefund/staff/${id}/lineitemid`, { params: serializeCbiDto(options, "generated.dto.LineItemFetchOptions") });
    return deserializeCbiDto<LineItemStaff | null>(data, "generated.dto.LineItemStaff");
  }

  async getLineItemRefundStaffTaxes(id: number, options?: LineItemRefundTaxFetchOptions): Promise<Array<LineItemRefundTaxStaff>> {
    const { data } = await this.client.get<unknown>(`/lineitemrefund/staff/${id}/taxes`, { params: serializeCbiDto(options, "generated.dto.LineItemRefundTaxFetchOptions") });
    return deserializeCbiDto<Array<LineItemRefundTaxStaff>>(data, "generated.dto.LineItemRefundTaxStaff");
  }

  async getLineItemRefundStaffOrderRefundId(id: number, options?: OrderRefundFetchOptions): Promise<OrderRefundStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitemrefund/staff/${id}/orderrefundid`, { params: serializeCbiDto(options, "generated.dto.OrderRefundFetchOptions") });
    return deserializeCbiDto<OrderRefundStaff | null>(data, "generated.dto.OrderRefundStaff");
  }

  async getLineItemRefundTaxStaffLineItemRefundId(id: number, options?: LineItemRefundFetchOptions): Promise<LineItemRefundStaff | null> {
    const { data } = await this.client.get<unknown>(`/lineitemrefundtax/staff/${id}/lineitemrefundid`, { params: serializeCbiDto(options, "generated.dto.LineItemRefundFetchOptions") });
    return deserializeCbiDto<LineItemRefundStaff | null>(data, "generated.dto.LineItemRefundStaff");
  }

  async getLineItemRefundTaxStaffTaxId(id: number, options?: LineItemTaxFetchOptions): Promise<LineItemTaxStaff> {
    const { data } = await this.client.get<unknown>(`/lineitemrefundtax/staff/${id}/taxid`, { params: serializeCbiDto(options, "generated.dto.LineItemTaxFetchOptions") });
    return deserializeCbiDto<LineItemTaxStaff>(data, "generated.dto.LineItemTaxStaff");
  }

  async addPurchaseMember(request: OrderAddPurchaseRequest): Promise<LineItemRef> {
    const { data } = await this.client.post<unknown>(`/order/member/addpurchase`, serializeCbiDto(request, "generated.dto.OrderAddPurchaseRequest"));
    return deserializeCbiDto<LineItemRef>(data, "generated.dto.LineItemRef");
  }

  async addToCartStaff(request: OrderAddToCartRequest): Promise<LineItemRef> {
    const { data } = await this.client.post<unknown>(`/order/staff/addtocart`, serializeCbiDto(request, "generated.dto.OrderAddToCartRequest"));
    return deserializeCbiDto<LineItemRef>(data, "generated.dto.LineItemRef");
  }

  async addToCartMember(request: OrderAddToCartRequest): Promise<LineItemRef> {
    const { data } = await this.client.post<unknown>(`/order/member/addtocart`, serializeCbiDto(request, "generated.dto.OrderAddToCartRequest"));
    return deserializeCbiDto<LineItemRef>(data, "generated.dto.LineItemRef");
  }

  async applyDiscountFromCodeStaff(request: OrderApplyDiscountFromCodeRequest): Promise<void> {
    await this.client.post(`/order/staff/applydiscountfromcode`, serializeCbiDto(request, "generated.dto.OrderApplyDiscountFromCodeRequest"));
  }

  async applyDiscountFromCodeMember(request: OrderApplyDiscountFromCodeRequest): Promise<void> {
    await this.client.post(`/order/member/applydiscountfromcode`, serializeCbiDto(request, "generated.dto.OrderApplyDiscountFromCodeRequest"));
  }

  async applyDiscountToLineItemsStaff(request: OrderApplyDiscountToLineItemsRequest): Promise<void> {
    await this.client.post(`/order/staff/applydiscounttolineitems`, serializeCbiDto(request, "generated.dto.OrderApplyDiscountToLineItemsRequest"));
  }

  async cancelStaff(order: OrderRef): Promise<void> {
    await this.client.post(`/order/staff/cancel`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async cancelMember(order: OrderRef): Promise<void> {
    await this.client.post(`/order/member/cancel`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async changeBuyerStaff(request: OrderChangeBuyerRequest): Promise<OrderRef> {
    const { data } = await this.client.post<unknown>(`/order/staff/changebuyer`, serializeCbiDto(request, "generated.dto.OrderChangeBuyerRequest"));
    return deserializeCbiDto<OrderRef>(data, "generated.dto.OrderRef");
  }

  async checkoutOrderMember(order: OrderRef): Promise<OrderMember> {
    const { data } = await this.client.post<unknown>(`/order/member/checkoutorder`, serializeCbiDto(order, "generated.dto.OrderRef"));
    let result = data as OrderMember;
    result = deserializeCbiDto<OrderMember>(result, "generated.dto.OrderMember");
    return result;
  }

  async checkoutReadinessMember(order: OrderRef): Promise<CheckoutReadiness> {
    const { data } = await this.client.post<unknown>(`/order/member/checkoutreadiness`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<CheckoutReadiness>(data, "generated.dto.CheckoutReadiness");
  }

  async configureDeliveryStaff(request: OrderConfigureDeliveryRequest): Promise<PurchaseDeliveryRef> {
    const { data } = await this.client.post<unknown>(`/order/staff/configuredelivery`, serializeCbiDto(request, "generated.dto.OrderConfigureDeliveryRequest"));
    return deserializeCbiDto<PurchaseDeliveryRef>(data, "generated.dto.PurchaseDeliveryRef");
  }

  async configureDeliveryMember(request: OrderConfigureDeliveryRequest): Promise<PurchaseDeliveryRef> {
    const { data } = await this.client.post<unknown>(`/order/member/configuredelivery`, serializeCbiDto(request, "generated.dto.OrderConfigureDeliveryRequest"));
    return deserializeCbiDto<PurchaseDeliveryRef>(data, "generated.dto.PurchaseDeliveryRef");
  }

  async configureReceiptDeliveryStaff(request: OrderConfigureReceiptDeliveryRequest): Promise<void> {
    await this.client.post(`/order/staff/configurereceiptdelivery`, serializeCbiDto(request, "generated.dto.OrderConfigureReceiptDeliveryRequest"));
  }

  async confirmDeliveryStaff(lineItem: LineItemRef): Promise<void> {
    await this.client.post(`/order/staff/confirmdelivery`, serializeCbiDto(lineItem, "generated.dto.LineItemRef"));
  }

  async eligibleDiscountsStaff(order: OrderRef): Promise<Array<DiscountCandidate>> {
    const { data } = await this.client.post<unknown>(`/order/staff/eligiblediscounts`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<Array<DiscountCandidate>>(data, "generated.dto.DiscountCandidate");
  }

  async emailReceiptStaff(order: OrderRef): Promise<void> {
    await this.client.post(`/order/staff/emailreceipt`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async getOpenOrderMember(): Promise<OrderRef> {
    const { data } = await this.client.post<unknown>(`/order/member/getopenorder`);
    return deserializeCbiDto<OrderRef>(data, "generated.dto.OrderRef");
  }

  async openOrderStaff(request: OrderOpenOrderRequest): Promise<OrderRef> {
    const { data } = await this.client.post<unknown>(`/order/staff/openorder`, serializeCbiDto(request, "generated.dto.OrderOpenOrderRequest"));
    return deserializeCbiDto<OrderRef>(data, "generated.dto.OrderRef");
  }

  async orderLineDetailsStaff(order: OrderRef): Promise<Array<OrderLineDetail>> {
    const { data } = await this.client.post<unknown>(`/order/staff/orderlinedetails`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<Array<OrderLineDetail>>(data, "generated.dto.OrderLineDetail");
  }

  async orderLineDetailsMember(order: OrderRef): Promise<Array<OrderLineDetail>> {
    const { data } = await this.client.post<unknown>(`/order/member/orderlinedetails`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<Array<OrderLineDetail>>(data, "generated.dto.OrderLineDetail");
  }

  async payStaff(order: OrderRef): Promise<void> {
    await this.client.post(`/order/staff/pay`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async payMember(order: OrderRef): Promise<void> {
    await this.client.post(`/order/member/pay`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async removeFromCartStaff(lineItem: LineItemRef): Promise<void> {
    await this.client.post(`/order/staff/removefromcart`, serializeCbiDto(lineItem, "generated.dto.LineItemRef"));
  }

  async removeFromCartMember(lineItem: LineItemRef): Promise<void> {
    await this.client.post(`/order/member/removefromcart`, serializeCbiDto(lineItem, "generated.dto.LineItemRef"));
  }

  async reopenStaff(order: OrderRef): Promise<void> {
    await this.client.post(`/order/staff/reopen`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async reopenMember(order: OrderRef): Promise<void> {
    await this.client.post(`/order/member/reopen`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async retryDeliveriesStaff(order: OrderRef): Promise<void> {
    await this.client.post(`/order/staff/retrydeliveries`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async retryReceiptDeliveryStaff(order: OrderRef): Promise<void> {
    await this.client.post(`/order/staff/retryreceiptdelivery`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async startCartStaff(person: PersonRef | null): Promise<OrderRef> {
    const { data } = await this.client.post<unknown>(`/order/staff/startcart`, serializeCbiDto(person, "generated.dto.PersonRef"));
    return deserializeCbiDto<OrderRef>(data, "generated.dto.OrderRef");
  }

  async switchOrderSourceStaff(request: OrderSwitchOrderSourceRequest): Promise<OrderRef> {
    const { data } = await this.client.post<unknown>(`/order/staff/switchordersource`, serializeCbiDto(request, "generated.dto.OrderSwitchOrderSourceRequest"));
    return deserializeCbiDto<OrderRef>(data, "generated.dto.OrderRef");
  }

  async unapplyDiscountFromLineItemsStaff(request: OrderUnapplyDiscountFromLineItemsRequest): Promise<void> {
    await this.client.post(`/order/staff/unapplydiscountfromlineitems`, serializeCbiDto(request, "generated.dto.OrderUnapplyDiscountFromLineItemsRequest"));
  }

  async unapplyLineItemDiscountStaff(request: OrderUnapplyLineItemDiscountRequest): Promise<void> {
    await this.client.post(`/order/staff/unapplylineitemdiscount`, serializeCbiDto(request, "generated.dto.OrderUnapplyLineItemDiscountRequest"));
  }
}

export const useOrderApi = (): OrderApi => new OrderApiClient();
